import type { LocationServiceTypeClientDto, WaitlistOrderClientDto } from 'service-api/src/types/api'

export interface PetServiceTypes {
  primaryOfferDisplayName?: string
  inStayServiceTypes: string[]
}

export interface TrainingVisit {
  invoice: WaitlistInvoice
  pet: WaitlistPet
  primaryOfferDisplayName: string
}

type WaitlistInvoice = NonNullable<WaitlistOrderClientDto['invoices']>[number]
type WaitlistInvoiceItem = NonNullable<WaitlistInvoice['items']>[number]
type WaitlistPet = NonNullable<WaitlistOrderClientDto['pets']>[number]

export const getServiceTypesByPetId = (
  order: WaitlistOrderClientDto,
  locationServiceTypes: LocationServiceTypeClientDto[]
) => {
  const primaryServiceTypeId = order.primaryServiceType?.id
  const locationServiceTypeById = new Map(locationServiceTypes.map((st) => [st.id, st]))
  const serviceTypesByPetId = new Map<string, PetServiceTypes>()

  order.invoices?.forEach((invoice) => {
    const primaryOffer = invoice.items?.find(
      (item): item is WaitlistInvoiceItem => item.type === 'OFFER' && item.offerType === 'PRIMARY'
    )

    if (!primaryOffer) return

    const locationServiceTypeId = primaryOffer.locationServiceTypeId || invoice.locationServiceTypeId
    const isPrimaryServiceType = locationServiceTypeId === primaryServiceTypeId
    const inStayServiceType = locationServiceTypeId
      ? locationServiceTypeById.get(locationServiceTypeId)?.serviceType?.name
      : undefined

    if (!isPrimaryServiceType && !inStayServiceType) return

    invoice.petRelations?.forEach(({ waitlistPetId }) => {
      if (!waitlistPetId) return

      const petServiceTypes = serviceTypesByPetId.get(waitlistPetId) ?? {
        inStayServiceTypes: [],
      }

      if (isPrimaryServiceType && petServiceTypes.primaryOfferDisplayName === undefined) {
        petServiceTypes.primaryOfferDisplayName = primaryOffer.offer?.displayName || ''
      } else if (inStayServiceType && !petServiceTypes.inStayServiceTypes.includes(inStayServiceType)) {
        petServiceTypes.inStayServiceTypes.push(inStayServiceType)
      }

      serviceTypesByPetId.set(waitlistPetId, petServiceTypes)
    })
  })

  return serviceTypesByPetId
}

export const getTrainingVisits = (
  order: WaitlistOrderClientDto,
  locationServiceTypes: LocationServiceTypeClientDto[]
) => {
  const trainingServiceTypeIds = new Set(
    locationServiceTypes
      .filter(({ serviceType }) => serviceType?.name === 'training')
      .map(({ id }) => id)
      .filter((id): id is string => !!id)
  )
  const petById = new Map(order.pets?.map((pet) => [pet.id, pet]))

  return (
    order.invoices?.flatMap((invoice) => {
      if (!invoice.locationServiceTypeId || !trainingServiceTypeIds.has(invoice.locationServiceTypeId)) return []

      const primaryOffer = invoice.items?.find(
        (item): item is WaitlistInvoiceItem => item.type === 'OFFER' && item.offerType === 'PRIMARY'
      )
      if (!primaryOffer) return []

      return (
        invoice.petRelations?.flatMap(({ waitlistPetId }) => {
          const pet = petById.get(waitlistPetId)
          if (!pet) return []

          return {
            invoice,
            pet,
            primaryOfferDisplayName: primaryOffer.offer?.displayName || '',
          }
        }) ?? []
      )
    }) ?? []
  )
}
